/** Share a Readiness Trial score as a branded 1080x1080 image (Web Share / download + WhatsApp). */
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Share2, Loader2 } from "lucide-react";

const drawCard = ({ trialNumber, score, correct, total }) => {
  const c = document.createElement("canvas");
  c.width = 1080; c.height = 1080;
  const ctx = c.getContext("2d");

  // background
  ctx.fillStyle = "#F7F4EF";
  ctx.fillRect(0, 0, 1080, 1080);
  // top band
  ctx.fillStyle = "#4A6E5B";
  ctx.fillRect(0, 0, 1080, 190);
  ctx.fillStyle = "#FFFFFF";
  ctx.font = 'bold 64px "Outfit Variable", Outfit, Arial, sans-serif';
  ctx.textAlign = "center";
  ctx.fillText("PassADC", 540, 118);

  ctx.fillStyle = "#5B5A55";
  ctx.font = '600 44px "Outfit Variable", Outfit, Arial, sans-serif';
  ctx.fillText(`Practice-Readiness Trial ${trialNumber}`, 540, 300);
  ctx.font = '400 34px "Outfit Variable", Outfit, Arial, sans-serif';
  ctx.fillText("140 questions · 4 hours · real ADC blueprint", 540, 360);

  // score circle
  ctx.beginPath();
  ctx.arc(540, 600, 190, 0, Math.PI * 2);
  ctx.strokeStyle = "#C66243";
  ctx.lineWidth = 16;
  ctx.stroke();
  ctx.fillStyle = "#2E2D2A";
  ctx.font = 'bold 150px "Outfit Variable", Outfit, Arial, sans-serif';
  ctx.fillText(`${score}%`, 540, 645);

  ctx.fillStyle = "#5B5A55";
  ctx.font = '500 40px "Outfit Variable", Outfit, Arial, sans-serif';
  ctx.fillText(`${correct} / ${total} correct`, 540, 870);

  ctx.fillStyle = "#4A6E5B";
  ctx.font = '600 36px "Outfit Variable", Outfit, Arial, sans-serif';
  ctx.fillText("Take the free readiness check → passadc.com", 540, 990);

  return new Promise((resolve) => c.toBlob(resolve, "image/png"));
};

export const ShareScoreButton = ({ trialNumber, score, correct, total }) => {
  const [busy, setBusy] = useState(false);

  const share = async () => {
    setBusy(true);
    try {
      const blob = await drawCard({ trialNumber, score, correct, total });
      const file = new File([blob], `passadc-trial-${trialNumber}-score.png`, { type: "image/png" });
      const text = `I scored ${score}% on PassADC Readiness Trial ${trialNumber} (${correct}/${total}) - full ADC-style mock. Try the free readiness check: https://passadc.com`;

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title: "My PassADC trial score" });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = file.name;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
        toast.success("Score card downloaded - attach it to your WhatsApp message");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not share your score");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button variant="outline" onClick={share} disabled={busy} className="gap-2" data-testid="share-score-btn">
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
      Share my score
    </Button>
  );
};
